import React, { useEffect, useState } from 'react';
import Select from 'react-select';
import { Card, Button, Row, Col, Form, FormControl, Modal, Spinner } from 'react-bootstrap';
import { FaDownload, FaSearch, FaEraser, FaFileInvoice, FaHashtag, FaCalendarAlt } from 'react-icons/fa';
import api from '../../api/apiweb-resumenmensualboletas.js';
import { TableSimple } from 'react-pagination-table';
import DatePicker, { registerLocale } from 'react-datepicker';
import es from 'date-fns/locale/es';
import { tiposDisponiblesEmpresa, labelTipoDocumento } from '../../constants/tiposDocumento';
import { formatoMiles } from '../../utils/format';
import selectStyles from '../../utils/selectStyles';
import 'react-datepicker/dist/react-datepicker.css';
import './styles.scss';

registerLocale('es', es);

const MonthInput = React.forwardRef(({ value, onClick }, ref) => (
  <div className="month-input-wrapper" onClick={onClick}>
    <FaCalendarAlt className="month-input-icon" />
    <FormControl readOnly ref={ref} value={value} className="month-input" />
  </div>
));

function Search(props) {
  const [docType, setDocType] = useState('');

  // Incluye los tipos habilitados para la empresa: boletas, facturas y las notas
  // de crédito/débito emitidas desde la web, que también suman al resumen mensual.
  const tiposDocOpciones = tiposDisponiblesEmpresa(props.empresas, undefined, { incluirNotas: true });
  const tipoUnico = tiposDocOpciones.length === 1 ? tiposDocOpciones[0].value : null;
  const docOptions = tiposDocOpciones.length > 1
    ? [{ value: '', label: 'Todos' }, ...tiposDocOpciones]
    : tiposDocOpciones;
  const [startDate, setStartDate] = useState(new Date());
  const [searching, setSearching] = useState(false);
  const [modalDescarga, setModalDescarga] = useState(false);
  const [mensajeFallo, setMensajeFallo] = useState(false);
  const [modalDescargaMensaje, setModalDescargaMensaje] = useState('');
  const [responseData, setResponseData] = useState(null);
  const [totales, setTotales] = useState(null);
  const __DEV__ = !process.env.NODE_ENV || process.env.NODE_ENV === 'development';

  useEffect(() => {
    if (tipoUnico) setDocType(tipoUnico);
  }, [tipoUnico]);

  const monthBounds = (date) => {
    const first = new Date(date.getFullYear(), date.getMonth(), 1).toISOString().split('T')[0];
    const last = new Date(date.getFullYear(), date.getMonth() + 1, 0).toISOString().split('T')[0];
    return { first, last };
  };

  const busqueda = async () => {
    if (!startDate) return;
    const { first, last } = monthBounds(startDate);
    setModalDescarga(true);
    setMensajeFallo(false);
    setModalDescargaMensaje('Procesando');
    setSearching(true);
    api.getData({ docType, startDate: first, endDate: last }).then(
      (data) => {
        if (__DEV__) { console.log(data.data.records); }
        setModalDescarga(false);
        setResponseData(data.data.records);
        setSearching(false);
      },
    ).catch(() => {
      setSearching(false);
      setModalDescargaMensaje('Error en la descarga');
      setMensajeFallo(true);
    });
  };

  const descarga = async () => {
    if (!startDate) return;
    const { first, last } = monthBounds(startDate);
    setModalDescarga(true);
    setMensajeFallo(false);
    setModalDescargaMensaje('Procesando');
    api.download({ docType, startDate: first, endDate: last }, setModalDescarga);
  };

  const limpiar = () => {
    setDocType(tipoUnico || '');
    setStartDate(new Date());
    setResponseData(null);
    setTotales(null);
  };

  const closeModal = () => {
    setModalDescarga(false);
  };

  useEffect(() => {
    if (responseData == null) {
      setTotales(null);
      return;
    }
    let cantidad = 0;
    let neto = 0;
    let iva = 0;
    let exento = 0;
    let total = 0;

    responseData.forEach((element) => {
      cantidad += parseInt(element.cantidad, 10) || 0;
      neto += parseInt(element.valorneto, 10) || 0;
      iva += parseInt(element.valoriva, 10) || 0;
      exento += parseInt(element.valorexento, 10) || 0;
      total += parseInt(element.valortotal, 10) || 0;

      element.tipodocumento = labelTipoDocumento(element.tipodocumento);
      element.cantidad = formatoMiles(element.cantidad);
      element.valorneto = formatoMiles(element.valorneto);
      element.valorexento = formatoMiles(element.valorexento);
      element.valoriva = formatoMiles(element.valoriva);
      element.valortotal = formatoMiles(element.valortotal);
    });

    setTotales({ cantidad, neto, iva, exento, total });
  }, [responseData]);

  return (
    <>
      <div className="Search resumen-mensual">
        <div className="page-header">
          <h1 className="page-title">Resumen Mensual de Documentos</h1>
          <p className="page-subtitle">Totales mensuales de documentos emitidos</p>
        </div>

        <Card className="search-card">
          <Card.Header className="search-card-header">
            <FaSearch className="me-2" />
            Filtros
          </Card.Header>
          <Card.Body>
            <Row className="g-3">
              <Col sm={12} md={6} lg={6}>
                <Form.Label htmlFor="docType">Tipo Documento</Form.Label>
                <Select
                  inputId="docType"
                  options={docOptions}
                  value={docOptions.find((o) => o.value === docType) || null}
                  onChange={(opt) => setDocType(opt ? opt.value : '')}
                  styles={selectStyles}
                  isSearchable={false}
                  placeholder="Seleccione"
                />
              </Col>
              <Col sm={12} md={6} lg={6}>
                <Form.Label>Mes</Form.Label>
                <DatePicker
                  selected={startDate}
                  onChange={(date) => setStartDate(date)}
                  dateFormat="MMMM yyyy"
                  showMonthYearPicker
                  showFullMonthYearPicker
                  locale="es"
                  customInput={<MonthInput />}
                />
              </Col>
            </Row>

            <div className="search-actions">
              <Button variant="primary" disabled={searching} onClick={busqueda}>
                <FaSearch className="me-2" />Buscar
              </Button>
              <Button variant="success" onClick={descarga}>
                <FaDownload className="me-2" />Descargar CSV
              </Button>
              <Button variant="outline-secondary" onClick={limpiar} className="ms-auto">
                <FaEraser className="me-2" />Limpiar
              </Button>
            </div>
          </Card.Body>
        </Card>

        {totales && (
          <div className="totales-grid">
            <div className="total-card">
              <div className="total-icon total-icon-neutral"><FaHashtag /></div>
              <div>
                <div className="total-label">Cantidad</div>
                <div className="total-value">{formatoMiles(totales.cantidad)}</div>
              </div>
            </div>
            <div className="total-card">
              <div className="total-label">Valor Neto</div>
              <div className="total-value">${formatoMiles(totales.neto)}</div>
            </div>
            <div className="total-card">
              <div className="total-label">Valor IVA</div>
              <div className="total-value">${formatoMiles(totales.iva)}</div>
            </div>
            <div className="total-card">
              <div className="total-label">Valor Exento</div>
              <div className="total-value">${formatoMiles(totales.exento)}</div>
            </div>
            <div className="total-card total-card-highlight">
              <div className="total-label">Valor Total</div>
              <div className="total-value">${formatoMiles(totales.total)}</div>
            </div>
          </div>
        )}

        <Card className="search-card mt-4">
          <Card.Header className="search-card-header">
            <FaFileInvoice className="me-2" />
            Resultados
            {responseData && responseData.length > 0 && (
              <span className="results-count">{responseData.length}</span>
            )}
          </Card.Header>
          <Card.Body>
            {responseData && responseData.length > 0 ? (
              <TableSimple
                className="resumenmensualboletas"
                headers={['Tipo Documento', 'Fecha Emisión', 'Cantidad', 'Valor Neto', 'Valor IVA', 'Valor Exento', 'Valor Total']}
                data={responseData}
                columns="tipodocumento.fechaemision.cantidad.valorneto.valoriva.valorexento.valortotal"
                perPageItemCount={10}
                partialPageCount={3}
                totalCount={responseData.length}
                arrayOption={[['size', 'all', ' ']]}
                nextPageText="Siguiente"
                prePageText="Anterior"
              />
            ) : (
              <div className="empty-state">
                <FaFileInvoice className="empty-state-icon" />
                <p className="empty-state-text">
                  {responseData === null
                    ? 'Selecciona un mes y presiona "Buscar"'
                    : 'No se encontraron documentos en el mes seleccionado'}
                </p>
              </div>
            )}
          </Card.Body>
        </Card>
      </div>

      <Modal show={modalDescarga} onHide={closeModal} centered>
        <Modal.Header closeButton={mensajeFallo}>
          <Modal.Title>Resumen mensual de documentos</Modal.Title>
        </Modal.Header>
        <Modal.Body className="text-center py-4">
          {!mensajeFallo && <Spinner animation="border" variant="primary" className="mb-3" />}
          <div className="modal-message">{modalDescargaMensaje}</div>
        </Modal.Body>
        {mensajeFallo && (
          <Modal.Footer>
            <Button variant="secondary" onClick={closeModal}>Cerrar</Button>
          </Modal.Footer>
        )}
      </Modal>
    </>
  );
}

export default Search;
